/** Parse #RGB / #RRGGBB (optional leading #) into 0–255 channels. */
export function parseHexColor(input: string): { r: number; g: number; b: number } | null {
  const raw = input.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(raw)) return null;
  const hex =
    raw.length === 3
      ? raw
          .split('')
          .map((ch) => `${ch}${ch}`)
          .join('')
      : raw;
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  };
}

function srgbChannelToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance (WCAG), 0 = black, 1 = white. */
export function relativeLuminance(hexColor: string): number | null {
  const rgb = parseHexColor(hexColor);
  if (!rgb) return null;
  const r = srgbChannelToLinear(rgb.r);
  const g = srgbChannelToLinear(rgb.g);
  const b = srgbChannelToLinear(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Pick black or white text for a solid hex background.
 * Falls back to white when the color cannot be parsed (FullCalendar default).
 */
export function contrastTextForBackground(background: string): string {
  const luminance = relativeLuminance(background);
  if (luminance == null) return '#ffffff';
  // ~WCAG threshold between black/white on solid fills
  return luminance > 0.179 ? '#1a1a1a' : '#ffffff';
}
